import { RudderAnalytics } from '@rudderstack/analytics-js'
import type { MaybeRef } from '@vueuse/shared'
import type { CookieRef } from '#app'
import { getCookieDomain } from '../utils/analytics'

export type PageViewEvent = {
  name?: string | null
  token?: string | null
  cid?: string | null
}

export type ButtonClickEvent = {
  elementId?: string | null
  text?: string | null
  linkTo?: string | null
}

export type LinkClickEvent = {
  elementId?: string | null
  linkText?: string | null
  linkTo?: string | null
}

export type FormInteractionEvent = {
  title?: string | null
  type?: string | null
  interactionType?: string | null
  text?: string | null
  lengthOfText?: number | null
  placeholderText?: string | null
}

enum AnalyticsEventType {
  PageView = 'PageView',
  ButtonClick = 'ButtonClick',
  FormInteraction = 'FormInteraction',
  LinkClick = 'LinkClick'
}

interface IAnalyticsClient {
  getAnonymousId(): string | null | undefined

  getSessionId(): string | null | undefined

  identify(userId: string, traits?: any): void

  pageView(event: PageViewEvent): void

  buttonClick(event: ButtonClickEvent): void

  linkClick(event: LinkClickEvent): void

  formInteraction(event: FormInteractionEvent): void

  track(eventType: string, properties?: any): void
}

class LoggingClient implements IAnalyticsClient {
  getAnonymousId(): string | null | undefined {
    return 'DEVELOPMENT'
  }

  getSessionId(): string | null | undefined {
    return 'DEVELOPMENT'
  }

  identify(userId: string, traits?: any): void {
    console.log('ANALYTICS :: identify', userId)
  }

  pageView(event: PageViewEvent): void {
    console.log('ANALYTICS :: pageView', event)
  }

  buttonClick(event: ButtonClickEvent): void {
    console.log('ANALYTICS :: buttonClick', event)
  }

  linkClick(event: LinkClickEvent): void {
    console.log('ANALYTICS :: linkClick', event)
  }

  formInteraction(event: FormInteractionEvent): void {
    console.log('ANALYTICS :: formInteraction', event)
  }

  track(eventType: string, properties?: any) {
    console.log(`ANALYTICS :: ${eventType}`, properties)
  }
}

class RudderStackClient implements IAnalyticsClient {
  private _fbp: CookieRef
  private _fbc: CookieRef

  constructor(private rudderanalytics: RudderAnalytics, private rsReady: Ref<boolean>) {
    this._fbp = useCookie('_fbp', { domain: getCookieDomain() })
    this._fbc = useCookie('_fbc', { domain: getCookieDomain() })
  }
  getAnonymousId(): string | null | undefined {
    return this.rsReady.value ? this.rudderanalytics.getAnonymousId() : null
  }

  getSessionId(): string | null | undefined {
    return this.rsReady.value ? this.rudderanalytics.getSessionId()?.toString() : null
  }

  getContext(): any {
    let context: any = {}

    if (this._fbp.value) {
      context = { ...context, fbp: this._fbp.value }
    }

    if (this._fbc.value) {
      context = { ...context, fbc: this._fbc.value }
    }

    return context
  }

  identify(userId: string, traits?: any): void {
    this.rudderanalytics.identify(userId, traits)
  }

  pageView(event: PageViewEvent): void {
    this.rudderanalytics.page(
      AnalyticsEventType.PageView,
      event.name,
      {
        token: event.token,
        cid: event.cid
      },
      this.getContext()
    )
  }

  buttonClick(event: ButtonClickEvent): void {
    this.track(AnalyticsEventType.ButtonClick, event)
  }

  linkClick(event: LinkClickEvent): void {
    this.track(AnalyticsEventType.LinkClick, event)
  }

  formInteraction(event: FormInteractionEvent): void {
    this.track(AnalyticsEventType.FormInteraction, event)
  }

  track(eventType: string, properties?: any): void {
    this.rudderanalytics.track(eventType, properties, this.getContext())
  }
}

const loggingInstance = new LoggingClient()

export class Analytics {
  private static _anonymousId: string | null = null

  static setAnonymousId(anonId: string) {
    Analytics._anonymousId = anonId
  }

  static get props() {
    return {
      eventData: [Function, Object]
    }
  }

  static getEventData(
    eventData: MaybeRef<(() => Record<string, any>) | Record<string, any> | null | undefined>
  ) {
    const ed = unref(eventData)
    if (_isNil(ed)) return {}
    if (_isFunction(ed)) return ed()
    return ed
  }

  static use() {
    const { $rs, $rsReady } = useNuxtApp()

    const client = computed(() =>
      // @ts-ignore
      !_isNil($rs) ? new RudderStackClient($rs, $rsReady) : loggingInstance
    )

    function getAnonymousId() {
      return client.value.getAnonymousId()
    }

    function getSessionId() {
      return client.value.getSessionId()
    }

    function pageView(event: PageViewEvent) {
      client.value.pageView(event)
    }

    function buttonClick(event: ButtonClickEvent): void {
      client.value.buttonClick(event)
    }

    function linkClick(event: LinkClickEvent): void {
      client.value.linkClick(event)
    }

    function formInteraction(event: FormInteractionEvent): void {
      client.value.formInteraction(event)
    }

    function identify(userId: string, traits?: any) {
      client.value.identify(userId, traits)
    }

    function track(eventType: string, properties?: any) {
      client.value.track(eventType, properties)
    }

    return {
      getAnonymousId,
      getSessionId,
      pageView,
      buttonClick,
      linkClick,
      formInteraction,
      identify,
      track
    }
  }
}
